import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, MapPinIcon, ArrowRightIcon, TrendingUpIcon, BookOpenIcon, BarChart3Icon } from 'lucide-react';
import { Header } from '../Header';
import { SiteFooter } from '../SiteFooter';
import { Button, FadeIn } from '../ui';
export function HitecPage() {
  return <div className="min-h-screen bg-white font-sans text-slate-800 flex flex-col" data-id="element-4354">
      <Header data-id="element-4355" />

      <main className="flex-1" data-id="element-4356">
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden bg-[#0A0F1F]" data-id="element-4357">
          {/* Ambient background */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-4358">
            <motion.div animate={{
            x: [0, 20, 0],
            y: [0, -15, 0]
          }} transition={{
            duration: 12,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-brand-teal/[0.15] rounded-full blur-[100px]" data-id="element-4359" />
            <motion.div animate={{
            x: [0, -20, 0],
            y: [0, 20, 0]
          }} transition={{
            duration: 15,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-1/4 -right-40 w-[500px] h-[500px] bg-brand-coral/[0.12] rounded-full blur-[100px]" data-id="element-4360" />
          </div>

          {/* Grid texture */}
          <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)'
        }} data-id="element-4361" />

          <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10 text-center" data-id="element-4362">
            <FadeIn data-id="element-4363">
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-2 pr-4 py-1.5 mb-8 backdrop-blur-sm" data-id="element-4364">
                <span className="bg-brand-coral text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-4365">
                  HITEC 2026
                </span>
                <span className="text-sm font-medium text-slate-300" data-id="element-4366">
                  The AI revenue layer for hotels &amp; resorts
                </span>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-4367">
              <h1 className="text-5xl md:text-7xl font-bold leading-[1.1] tracking-tight text-white mb-6 text-balance" data-id="element-4368">
                Turn every corporate offsite into{' '}
                <span className="text-brand-teal" data-id="element-4369">
                  measurable hotel revenue.
                </span>
              </h1>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-4370">
              <p className="text-lg md:text-xl text-slate-400 mb-10 leading-relaxed text-balance max-w-2xl mx-auto" data-id="element-4371">
                Offsite Plus is an AI-powered revenue layer for hotels and
                resorts. We convert corporate offsites, retreats, meetings, and
                leadership programs into higher-value room nights plus ancillary
                spend across F&amp;B, wellness, spa, activities, and experiences
                — booking more profitable group demand, not just more software.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-4372">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-4373">
                <a href="https://calendly.com/ashsingh/post-hitec-executive-briefing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center leading-none whitespace-nowrap rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed no-underline bg-brand-coral text-white hover:bg-orange-500 focus:ring-brand-coral shadow-lg shadow-brand-coral/20 text-base px-8 py-4 group w-full sm:w-auto" data-id="element-4374">
                  Book Executive Briefing
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-4375" />
                </a>
              </div>
            </FadeIn>

            <FadeIn delay={0.4} data-id="element-4376">
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400 font-medium" data-id="element-4377">
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/5" data-id="element-4378">
                  <MapPinIcon className="w-4 h-4 text-brand-teal" data-id="element-4379" />
                  San Antonio, Texas
                </div>
                <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full border border-white/5" data-id="element-4380">
                  <CalendarIcon className="w-4 h-4 text-brand-teal" data-id="element-4381" />
                  June 15–18, 2026
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        <section className="py-24 bg-white" data-id="element-4382">
          <div className="max-w-6xl mx-auto px-6 md:px-12" data-id="element-4383">
            <FadeIn data-id="element-4384">
              <div className="max-w-2xl mx-auto text-center mb-16" data-id="element-4385">
                <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4 tracking-tight" data-id="element-4386">
                  More than a better agenda — a{' '}
                  <span className="text-brand-teal" data-id="element-4387">revenue engine.</span>
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed" data-id="element-4388">
                  Offsite Plus turns the meetings you already host into your
                  highest-margin group business.
                </p>
              </div>
            </FadeIn>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-4389">
              <FadeIn delay={0.1} data-id="element-4390">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full" data-id="element-4391">
                  <div className="w-14 h-14 bg-brand-coral/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4392">
                    <TrendingUpIcon className="w-7 h-7 text-brand-coral" data-id="element-4393" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4394">
                    Higher Revenue Per Group
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4395">
                    Convert every offsite into higher-value room nights plus
                    ancillary spend across F&amp;B, wellness, spa, activities,
                    and experiences — capturing revenue most properties leave on
                    the table.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-4396">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full" data-id="element-4397">
                  <div className="w-14 h-14 bg-brand-teal/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4398">
                    <BookOpenIcon className="w-7 h-7 text-brand-teal" data-id="element-4399" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4400">
                    AI-Powered Learning Pathways
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4401">
                    Our AI builds customized learning pathways for every group —
                    personalized educational tracks that make your property the
                    destination corporate clients return to, and pay more for.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.3} data-id="element-4402">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full" data-id="element-4403">
                  <div className="w-14 h-14 bg-brand-navy/5 rounded-2xl flex items-center justify-center mb-6" data-id="element-4404">
                    <BarChart3Icon className="w-7 h-7 text-brand-navy" data-id="element-4405" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4406">
                    Proven, Measurable ROI
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4407">
                    Tie every program to hard numbers — spend lift, engagement,
                    and outcomes leadership can see — so you can defend premium
                    pricing and win repeat group bookings.
                  </p>
                </div>
              </FadeIn>
            </div>

            <FadeIn delay={0.4} data-id="element-4408">
              <div className="mt-14 text-center" data-id="element-4409">
                <a href="https://calendly.com/ashsingh/post-hitec-executive-briefing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center leading-none whitespace-nowrap rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 no-underline bg-brand-navy text-white hover:bg-slate-800 focus:ring-brand-navy shadow-lg text-base px-8 py-4 group" data-id="element-4410">
                  Can't meet at HITEC? Let's make time after the rush
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-4411" />
                </a>
              </div>
            </FadeIn>
          </div>
        </section>
      </main>

      <SiteFooter hideDeckCtas data-id="element-4412" />
    </div>;
}